/**
 * Newsletter-Editor V2 — Tiptap mit TextStyle + FontFamily + Color.
 *
 * Behebt zwei Owner-Bugs:
 *   1) Font-Wahl legte per ``insertContent(html)`` HTML als Text ab,
 *      sodass ``<span style="…">`` sichtbar im Editor und in der Mail
 *      landete. Jetzt kommt die offizielle FontFamily-Extension zum
 *      Einsatz, die eine echte Tiptap-Mark setzt.
 *   2) Textfarbe wurde gar nicht unterstützt. Ergänzt durch Color-
 *      Extension mit sicherer Farbpalette (kein Free-Color-Picker,
 *      damit die Palette zur Brand passt und der bleach-Sanitizer
 *      alles klarhält).
 *
 * Bild-Upload nutzt weiterhin die zentrale Upload-Infrastruktur.
 */
import React, { useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { TextStyle } from "@tiptap/extension-text-style";
import FontFamily from "@tiptap/extension-font-family";
import Color from "@tiptap/extension-color";
import {
  Bold, Italic, Underline as UnderlineIcon, Link as LinkIcon,
  Image as ImageIcon, List, ListOrdered, Heading1, Heading2,
  Undo, Redo, Type, Palette,
} from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const SAFE_FONTS = [
  { label: "Standard", value: "" },
  { label: "Arial", value: "Arial, Helvetica, sans-serif" },
  { label: "Helvetica", value: "Helvetica, Arial, sans-serif" },
  { label: "Georgia", value: "Georgia, 'Times New Roman', serif" },
  { label: "Times New Roman", value: "'Times New Roman', Times, serif" },
  { label: "Verdana", value: "Verdana, Geneva, sans-serif" },
  { label: "Trebuchet MS", value: "'Trebuchet MS', sans-serif" },
];

// Kompakte, mail-sichere Palette. Bewusst keine Free-Color-Auswahl.
const COLOR_SWATCHES = [
  { label: "Standard", value: "" },      // entfernt die Color-Mark
  { label: "Schwarz",  value: "#0f0f13" },
  { label: "Dunkelgrau", value: "#3f434e" },
  { label: "Weiß",     value: "#ffffff" },
  { label: "Cyan (Brand)", value: "#22d3ee" },
  { label: "Rot",      value: "#ef4444" },
  { label: "Grün",     value: "#22c55e" },
  { label: "Blau",     value: "#3b82f6" },
  { label: "Orange",   value: "#f97316" },
];

export function NewsletterEditor({ value, onChange, disabled = false }) {
  const [colorOpen, setColorOpen] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      TextStyle,
      FontFamily.configure({ types: ["textStyle"] }),
      Color.configure({ types: ["textStyle"] }),
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
      Image.configure({ HTMLAttributes: { style: "max-width:100%;height:auto;" } }),
    ],
    content: value || "",
    editable: !disabled,
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || "") !== current) editor.commands.setContent(value || "", false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const promptLink = () => {
    const prev = editor?.getAttributes("link")?.href || "";
    const url = window.prompt("Link-URL (leer = entfernen):", prev);
    if (url === null) return;
    if (url === "") { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const uploadImage = async (file) => {
    if (file.size > 3 * 1024 * 1024) { toast.error("Bild ist größer als 3 MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/portal/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = r.data?.url;
      if (!url) return;
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      editor.chain().focus().setImage({ src: `${backend}${url}` }).run();
      toast.success("Bild eingefügt");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload fehlgeschlagen"); }
  };

  const onPickImage = () => {
    const input = document.createElement("input");
    input.type = "file"; input.accept = "image/jpeg,image/png,image/webp";
    input.onchange = () => input.files?.[0] && uploadImage(input.files[0]);
    input.click();
  };

  if (!editor) return <div className="text-xs text-zinc-500">Editor lädt…</div>;

  const setFont = (family) => {
    if (family === "") editor.chain().focus().unsetFontFamily().run();
    else editor.chain().focus().setFontFamily(family).run();
  };

  const setColor = (hex) => {
    if (hex === "") editor.chain().focus().unsetColor().run();
    else editor.chain().focus().setColor(hex).run();
    setColorOpen(false);
  };

  return (
    <div className="border border-zinc-800 rounded-sm bg-zinc-950" data-testid="newsletter-editor">
      <div className="flex flex-wrap items-center gap-1 border-b border-zinc-800 p-1.5">
        <BtnToggle action={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive("bold")} icon={Bold} testId="nl-fmt-bold" title="Fett" />
        <BtnToggle action={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive("italic")} icon={Italic} testId="nl-fmt-italic" title="Kursiv" />
        <BtnToggle action={() => editor.chain().focus().toggleUnderline().run()} isActive={editor.isActive("underline")} icon={UnderlineIcon} testId="nl-fmt-underline" title="Unterstrichen" />
        <Divider />
        <BtnToggle action={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} isActive={editor.isActive("heading", { level: 1 })} icon={Heading1} testId="nl-fmt-h1" title="Überschrift 1" />
        <BtnToggle action={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} isActive={editor.isActive("heading", { level: 2 })} icon={Heading2} testId="nl-fmt-h2" title="Überschrift 2" />
        <Divider />
        <BtnToggle action={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive("bulletList")} icon={List} testId="nl-fmt-ul" title="Aufzählung" />
        <BtnToggle action={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive("orderedList")} icon={ListOrdered} testId="nl-fmt-ol" title="Nummerierte Liste" />
        <Divider />
        <BtnToggle action={promptLink} isActive={editor.isActive("link")} icon={LinkIcon} testId="nl-fmt-link" title="Link" />
        <BtnToggle action={onPickImage} icon={ImageIcon} testId="nl-fmt-image" title="Bild einfügen" />
        <Divider />
        <div className="inline-flex items-center gap-1">
          <Type size={13} className="text-zinc-500" />
          <select
            data-testid="nl-fmt-font"
            defaultValue=""
            onChange={(e) => { setFont(e.target.value); e.target.value = ""; }}
            className="h-7 text-xs bg-zinc-900 border border-zinc-800 rounded-sm text-zinc-300 px-1.5"
          >
            <option value="" disabled>Schriftart…</option>
            {SAFE_FONTS.map((f) => <option key={f.label} value={f.value}>{f.label}</option>)}
          </select>
        </div>
        <div className="relative inline-flex">
          <button type="button" data-testid="nl-fmt-color" onClick={() => setColorOpen(v => !v)}
            className="h-7 px-2 rounded-sm inline-flex items-center gap-1 border border-zinc-800 bg-zinc-900 text-zinc-300 hover:border-zinc-700"
            title="Textfarbe">
            <Palette size={13} />
            <span className="w-3 h-3 rounded-sm border border-zinc-700" style={{ background: editor.getAttributes("textStyle").color || "transparent" }} />
          </button>
          {colorOpen && (
            <div data-testid="nl-fmt-color-panel" className="absolute z-10 top-8 left-0 bg-zinc-900 border border-zinc-800 rounded-sm p-2 shadow-lg grid grid-cols-3 gap-1 w-40">
              {COLOR_SWATCHES.map((c) => (
                <button key={c.label} type="button" onClick={() => setColor(c.value)}
                  data-testid={`nl-fmt-color-${c.label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                  title={c.label}
                  className="h-8 w-full rounded-sm border border-zinc-800 hover:border-cyan-400 flex items-center justify-center text-[10px]"
                  style={c.value ? { background: c.value, color: c.value === "#ffffff" ? "#000" : "#fff" } : {}}>
                  {c.value ? "" : "Standard"}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="ml-auto flex items-center gap-1">
          <BtnToggle action={() => editor.chain().focus().undo().run()} icon={Undo} testId="nl-fmt-undo" title="Rückgängig" />
          <BtnToggle action={() => editor.chain().focus().redo().run()} icon={Redo} testId="nl-fmt-redo" title="Wiederherstellen" />
        </div>
      </div>
      <div className="tiptap-body px-3 py-3 min-h-[220px] text-sm text-zinc-100">
        <EditorContent editor={editor} />
      </div>
      <div className="border-t border-zinc-800 px-3 py-2 text-[11px] text-zinc-500">
        Platzhalter: <code className="text-cyan-400">{"{{first_name}}"}</code> · <code className="text-cyan-400">{"{{unsubscribe_url}}"}</code> (wird pro Empfänger ersetzt)
      </div>
    </div>
  );
}

function BtnToggle({ action, isActive, icon: Icon, testId, title }) {
  return (
    <button type="button" onClick={action} title={title} data-testid={testId}
      className={`h-7 min-w-7 px-1.5 rounded-sm inline-flex items-center justify-center border transition-colors ${
        isActive ? "bg-cyan-500/20 border-cyan-500 text-cyan-300"
                 : "bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-zinc-700 hover:text-zinc-100"}`}>
      <Icon size={14} />
    </button>
  );
}
function Divider() { return <div className="mx-1 h-5 w-px bg-zinc-800" />; }
